import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Micro from "@amcharts/amcharts5/themes/Micro";
import { useLayoutEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { timeUnitDurations } from "@amcharts/amcharts5/.internal/core/util/Time";
import { InfractionGranularity } from "../../store/types/infraction";
import { INFRACTION_TYPES } from "../../utils/constants/constants";
import { formatNumberWithComma } from "../../utils/functions/formatters";

interface SparklinesChartProps {
  data: any[];
  title: string;
  loading: boolean;
  granularity: InfractionGranularity;
}

function createValueChart(div: HTMLElement, data: any, color: string) {
  let root = am5.Root.new(div);
  root.setThemes([am5themes_Micro.new(root)]);
  
  let chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none"
    })
  );
  
  chart.plotContainer.set("wheelable", false);
  chart.zoomOutButton.set("forceHidden", true);
  
  let xAxis = chart.xAxes.push(am5xy.DateAxis.new(root, {
    maxDeviation: 0,
    baseInterval: { timeUnit: "day", count: 1 },
    renderer: am5xy.AxisRendererX.new(root, {})
  }));
  
  let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
    strictMinMax: true,
    extraMax: 0.02,
    extraMin: 0.02,
    renderer: am5xy.AxisRendererY.new(root, {})
  }));
  
  let series = chart.series.push(am5xy.LineSeries.new(root, {
    xAxis: xAxis,
    yAxis: yAxis,
    valueYField: "value",
    valueXField: "date",
    stroke: am5.color(color)
  }));
  
  series.strokes.template.setAll({
    strokeWidth: 2
  });
  
  series.data.setAll(data);
  return () => root.dispose();
}

function createVolumeChart(div: HTMLElement, data: any, granularity: InfractionGranularity) {
  let root = am5.Root.new(div);

  root.setThemes([
    am5themes_Micro.new(root)
  ]);

  let chart = root.container.children.push(am5xy.XYChart.new(root, {
    panX: false,
    panY: false,
    wheelX: "none",
    wheelY: "none"
  }));
  
  chart.plotContainer.set("wheelable", false);
  chart.zoomOutButton.set("forceHidden", true);

  let xAxis = chart.xAxes.push(am5xy.DateAxis.new(root, {
    maxDeviation: 0,
    baseInterval: { timeUnit: granularity, count: 1 },
    renderer: am5xy.AxisRendererX.new(root, {})
  }));

  let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
    renderer: am5xy.AxisRendererY.new(root, {})
  }));

  let series = chart.series.push(am5xy.ColumnSeries.new(root, {
    xAxis: xAxis,
    yAxis: yAxis,
    valueYField: "volume",
    valueXField: "date",
    
    fill: am5.color('#d4ecff')
  }));
  
  series.data.setAll(data);
}

const SparklinesChart: React.FC<SparklinesChartProps> = ({
  data,
  title,
  loading,
  granularity = 'day'
}) => {
  useLayoutEffect(() => {
    let div = document.getElementById("chartdiv") ?? new HTMLElement();
    div.innerHTML = '';

    for (const property in data) {
      if (!INFRACTION_TYPES.find((infraction) => infraction.value === property)) continue;
      let ticker = property;
      let content = data[property];
      let positive = am5.color(0xDA1E27);
      let negative = am5.color(0x51A112);

      content = content.map((item: any) => {
        const date = granularity === InfractionGranularity.day ? new Date(item.date) : new Date(item.date.split('-')[0], item.date.split('-')[1] - 2, 1);
        am5.time.add(date, granularity as keyof typeof timeUnitDurations, 1);
        return {
          date: date.getTime(),
          value: item.value,
          volume: item.volume
        };
      });
    
      let change = Math.round((content[content.length - 1].value / content[0].value - 1) * 1000) / 10;
      
      if(isNaN(change)) {
        change = -100
      }  else if(!isFinite(change)) {
        change = 100;
      }

      let color = change < 0 ? negative : positive;
      const startValue = content[0].value;
      const endValue = content[content.length - 1].value;

      let div = document.getElementById("chartdiv") ?? new HTMLElement();
      div.style.overflow = "auto";
    
      let row = document.createElement("div");
      row.style.borderBottom = "1px solid #E9EAED";
      row.style.clear = "left";
      div.appendChild(row);
    
      let col1 = document.createElement("div");
      col1.innerHTML = INFRACTION_TYPES.find((i => i.value === ticker))?.label ?? '-';
      col1.style.fontSize = "0.875rem";
      col1.style.color = "#7B809A";
      col1.style.width = "10%";
      col1.style.paddingTop = "1.5em";
      col1.style.paddingBottom = "1.5em";
      col1.style.marginRight = "1.5em";
      col1.style.float = "left";
      row.appendChild(col1);
    
      let col3 = document.createElement("div");
      col3.style.width = "65%";
      col3.style.height = "50px";
      col3.style.float = "left";
      row.appendChild(col3);
      createValueChart(col3, content, color.toCSSHex());

      let col4 = document.createElement("div");
      col4.innerHTML = `<div style="margin-bottom: 0.5em;"><span style="font-size:14px;padding:4px 7px 4px 7px;border-radius:50px;background-color:${color};color:white">${formatNumberWithComma(change.toString()) + "%"}</span></div><div style="margin-bottom: 1em;"><span style="font-size:11px; padding:4px 7px 4px 7px;border-radius:50px; border-style: solid; border-color:red;color:gray">de ${formatNumberWithComma(startValue)} para ${formatNumberWithComma(endValue)}</span></div>`;
      col4.style.width = `20%`;
      col4.style.paddingTop = "2em";
      col4.style.paddingLeft = "10px";
      col4.style.paddingTop = "15px";
      col4.style.textAlign = "center";
      col4.style.float = "left";
      row.appendChild(col4);
    }
}, [data]);

  return (
    <div>
      <div className="text-dark-blue font-bold text-2xl mb-3 pt-3 pb-5">{title}</div>
      <div
        id="chartdiv"
        style={{ width: "100%", height: "500px", display: loading || data.length === 0 ? "none" : "block" }}
      >
      </div>
      {!loading && data.length === 0 ? (
          <div style={{ height: "300px" }} className="flex justify-center items-center h-full">
            Nenhuma dado encontrado
          </div>
        ) : null}
      {loading && <Skeleton height={300} />}
    </div>
  );
};

export default SparklinesChart;
